import { useContext, useEffect, useState } from 'react';
import { Input } from '@acko-tech/building-blocks.ui.input';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { DeviceCategory, NewPlanStages } from '../constants';
import { inputRegexValidation, phoneNumberValidation } from 'utils/validation';
import {
    SegmentPages,
    SegmentTrackEvents,
    SegmentViewEvents,
    SegmentEventNameTypes,
    SegmentEventButtonNames,
    SegmentEventActionTypes,
    SegmentEventActionNames,
    PRODUCT,
    SUB_PRODUCT_NAME,
    SegmentEventFieldTypes,
} from 'utils/segment/constants';
import { MPosContext } from 'utils/context';
import { useSegmentViewEvents } from 'hooks/useSegmentView';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import Checkbox from 'library/checkbox';
import { getLabels, isValidIMEI } from './utils';
import { useRouter } from 'next/router';
import SnackBar from 'library/snackbar';
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics';
import { isEWPlan } from '../documentUpload/utils';
import Image from 'next/image';
import { getImageBasePath } from 'utils/generics';
import { Partners } from 'utils/constants';
import * as Styles from './styles';

function ScanIMEI() {
    const router = useRouter();
    const {
        partnerId,
        dealerType,
        planDetails,
        setPlanDetails,
        setActiveStage,
    } = useContext(MPosContext);
    const { isMobile } = useResponsiveDevice();

    const { phone } = planDetails?.customer;
    const { serial_number, is_device_verified } = planDetails.device;

    const { title, label, verifyLabel } = getLabels(planDetails.device.type);

    const [imei, setImei] = useState(serial_number || '');
    // const [confirmIMEI, setConfirmIMEI] = useState(serial_number || '');
    // const [showConfirmIMEI, setShowConfirmIMEI] = useState(false);
    const [isDeviceVerified, setIsDeviceVerified] =
        useState(is_device_verified);
    const [customerPhoneNumber, setCustomerPhoneNumber] = useState(phone || '');
    const [isChecked, setIsChecked] = useState(false);
    const [isContinueButtonDisabled, setIsContinueButtonDisabled] =
        useState(true);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [validationErr, setValidationErr] = useState({
        serial_number: '',
        confirm_serial_number: '',
        phone: '',
    });
    const [onClickSerialNumber, setOnClickSerialNumber] = useState(false);
    const [isVerifyingDevice, setIsVerifyingDevice] = useState(false);

    /**
     * view segment hook
     */
    useSegmentViewEvents(SegmentViewEvents.VIEW_SERIAL_NUMBER_SCAN, {
        page: SegmentPages.NEW_PLAN_SERIAL_NUMBER,
        product: 'pos',
        journey: 'buy',
        platform: 'mweb',
        partner: partnerId,
        type: dealerType,
    });

    useEffect(() => {
        if (onClickSerialNumber) {
            /**
             * Segment event
             */
            trackEvent(SegmentEventNameTypes.ACTION, {
                page_url: window?.location?.href || '',
                button_name: SegmentEventButtonNames.CLICK_SERIAL_NUMBER,
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name: SegmentEventActionNames.CLICK_SERIAL_NUMBER,
                product: PRODUCT,
                sub_product: SUB_PRODUCT_NAME,
                platform: isMobile ? 'mweb' : 'web',
                device: navigator.userAgent || '',
                partner: partnerId,
                field_selected: SegmentEventFieldTypes.TEXT,
                plan_id: planDetails.plan.plan_id,
                device_model: planDetails.device.name,
                category: planDetails.device.type,
            });
        }
    }, [onClickSerialNumber]);

    // useEffect(() => {
    //     if (onClickConfirmSerialNumber) {
    //         /**
    //          * Segment event
    //          */
    //         trackEvent(SegmentTrackEvents.TAP_LBL_CONFIRM_SERIAL_NUMBER, {
    //             page: SegmentPages.NEW_PLAN_SERIAL_NUMBER,
    //             partner: partnerId,
    //             product: 'pos',
    //             journey: 'buy',
    //             platform: 'mweb',
    //             type: dealerType,
    //             plan_id: planDetails.plan.plan_id,
    //             device_model: planDetails.device.name,
    //             category: planDetails.device.type,
    //         });
    //     }
    // }, [onClickConfirmSerialNumber]);

    useEffect(() => {
        setImei(serial_number);
        setIsDeviceVerified(
            partnerId === Partners.HERO_VIDA ? true : is_device_verified
        );
    }, [serial_number, is_device_verified]);

    useEffect(() => {
        if (partnerId === Partners.HERO_VIDA) {
            setIsDeviceVerified(true);
        }
    }, [partnerId]);

    useEffect(() => {
        if (
            !(
                inputRegexValidation(imei) &&
                inputRegexValidation(customerPhoneNumber) &&
                phoneNumberValidation(customerPhoneNumber) &&
                imei &&
                isValidIMEI(imei, partnerId) &&
                isChecked &&
                isDeviceVerified
            )
        ) {
            if (planDetails.device.type === DeviceCategory.MOBILE) {
                if (isValidIMEI(imei)) {
                    setIsContinueButtonDisabled(false);
                } else {
                    setIsContinueButtonDisabled(true);
                }
            }
            setIsContinueButtonDisabled(true);
        } else {
            setIsContinueButtonDisabled(false);
        }
    }, [imei, isChecked, isDeviceVerified, customerPhoneNumber]);

    useEffect(() => {
        if (!isContinueButtonDisabled) {
            setValidationErr({
                serial_number: '',
                confirm_serial_number: '',
                phone: '',
            });
        }
    }, [isContinueButtonDisabled]);

    useEffect(() => {
        if (customerPhoneNumber.length > 0) {
            if (!phoneNumberValidation(customerPhoneNumber)) {
                setValidationErr({
                    ...validationErr,
                    phone: 'Please enter a valid phone number',
                });
            } else {
                setValidationErr({
                    ...validationErr,
                    phone: '',
                });
            }
        } else {
            setValidationErr({
                ...validationErr,
                phone: '',
            });
        }
    }, [customerPhoneNumber]);

    useEffect(() => {
        const validationErrCopy = { ...validationErr };
        if (imei.length === 0) {
            return;
        }
        if (!inputRegexValidation(imei)) {
            if (partnerId === Partners.REALME) {
                validationErrCopy['serial_number'] =
                    'Please enter a valid serial number';
            } else {
                validationErrCopy['serial_number'] =
                    planDetails.device.type === 'mobile' ||
                    planDetails.device.type === 'tablet'
                        ? 'Please enter a valid 15 digit IMEI number'
                        : 'Please enter a valid serial number';
            }
            setValidationErr(validationErrCopy);
            setIsDeviceVerified(false);
            // setShowConfirmIMEI(false);
            return;
        }

        if (partnerId === Partners.REALME) {
            if (!(imei?.length >= 8 && imei?.length <= 20)) {
                validationErrCopy['serial_number'] =
                    'Please enter a valid serial number';
                setIsDeviceVerified(false);
                // setShowConfirmIMEI(false);
            } else {
                validationErrCopy['serial_number'] = '';
            }
        } else if (!isValidIMEI(imei)) {
            validationErrCopy['serial_number'] =
                planDetails.device.type === 'mobile' ||
                planDetails.device.type === 'tablet'
                    ? 'Please enter a valid 15 digit IMEI number'
                    : 'Please enter a valid serial number';
            setIsDeviceVerified(false);
        } else {
            validationErrCopy['serial_number'] = '';
        }
        setValidationErr(validationErrCopy);
    }, [imei]);

    const onChangeSerialNumber = (e: any) => {
        setImei(e.target.value);
        setIsDeviceVerified(false);
    };

    const onBlurSerialNumber = () => {
        if (
            planDetails.device.type === DeviceCategory.MOBILE ||
            partnerId === Partners.REALME
        ) {
            if (
                isValidIMEI(imei) ||
                (partnerId === Partners.REALME &&
                    imei.length >= 8 &&
                    imei.length <= 20)
            ) {
                import('./apis').then(async (req) => {
                    setIsVerifyingDevice(true);

                    const { status, statusCode, errMsg } =
                        await req.verifyDevice(
                            partnerId,
                            planDetails.device.type,
                            imei,
                            isEWPlan(planDetails.plan.plan_id?.toLowerCase()),
                            planDetails.device.model_mapping_name,
                            planDetails.plan.plan_id,
                            planDetails.plan.program_id
                        );
                    if (statusCode === 401) {
                        router.push('/login');
                        setIsVerifyingDevice(false);
                        setSnackbar({
                            type: 'failure',
                            message:
                                'Token expired. Redirecting to login page.',
                        });
                        return;
                    }
                    setIsDeviceVerified(status);
                    setIsVerifyingDevice(false);
                    if (!status) {
                        setSnackbar({
                            type: 'failure',
                            message:
                                errMsg ||
                                (partnerId === Partners.REALME ||
                                planDetails?.device?.type ===
                                    DeviceCategory.MOBILE
                                    ? 'Invalid serial number. Please check the serial number'
                                    : 'Invalid IMEI. Please check the imei value'),
                        });
                        setValidationErr({
                            ...validationErr,
                            serial_number:
                                errMsg ||
                                (partnerId === Partners.REALME ||
                                planDetails?.device?.type ===
                                    DeviceCategory.MOBILE
                                    ? 'Please enter a valid serial number'
                                    : 'Please enter a valid IMEI number'),
                        });
                    }
                });
            }
        }
    };

    const onSubmit = () => {
        setPlanDetails({
            ...planDetails,
            customer: {
                ...planDetails.customer,
                phone: customerPhoneNumber,
            },
            device: {
                ...planDetails.device,
                serial_number: imei,
                is_device_verified: isDeviceVerified,
            },
        });
        setActiveStage(NewPlanStages.DOCUMENT_UPLOAD);
        /**
         * Segment event
         */
        trackEvent(SegmentTrackEvents.TAP_CONTINUE_SERIAL_NUMBER_SCAN_PAGE, {
            page: SegmentPages.NEW_PLAN_SERIAL_NUMBER,
            partner: partnerId,
            product: 'pos',
            journey: 'buy',
            platform: 'mweb',
            type: dealerType,
            plan_id: planDetails.plan.plan_id,
            device_model: planDetails.device.name,
            category: planDetails.device.type,
        });
    };

    return (
        <Styles.Wrapper>
            <Styles.ScanIMEINumber>{title}</Styles.ScanIMEINumber>
            {/* <Scanner /> */}
            {/* <Styles.SplitFlex>
                <Styles.Hr />
                <Styles.OrText>Or</Styles.OrText>
                <Styles.Hr />
            </Styles.SplitFlex> */}
            <Styles.InputWrapper
                onClick={() => setOnClickSerialNumber(true)}
                onBlur={onBlurSerialNumber}
            >
                {isDeviceVerified ? (
                    <Input
                        className={''}
                        labelText={label}
                        name={label}
                        type={'text'}
                        value={imei}
                        onChange={onChangeSerialNumber}
                        errMsg={validationErr.serial_number}
                        hasError={validationErr.serial_number.length > 0}
                        disableCopy={true}
                        disablePaste={true}
                        leftElement={
                            isDeviceVerified ? (
                                <Styles.VerifiedIMEI>
                                    <Image
                                        width={'16px'}
                                        height={'16px'}
                                        alt={''}
                                        src={getImageBasePath(
                                            '/icons/greenTickFill.svg'
                                        )}
                                    />
                                </Styles.VerifiedIMEI>
                            ) : (
                                <></>
                            )
                        }
                    />
                ) : (
                    <Input
                        className={''}
                        labelText={label}
                        name={label}
                        type={'text'}
                        value={imei}
                        onChange={(e) => setImei(e.target.value)}
                        errMsg={validationErr.serial_number}
                        hasError={validationErr.serial_number.length > 0}
                        disableCopy={true}
                        disablePaste={true}
                        rightElement={
                            isVerifyingDevice ? (
                                <Image
                                    src={getImageBasePath(
                                        '/gif/greenCircleLoader.gif'
                                    )}
                                    alt="Loading"
                                    width={20}
                                    height={20}
                                />
                            ) : null
                        }
                    />
                )}
                {isVerifyingDevice && (
                    <Styles.LoaderTextWrapper>
                        {'Verifying the IMEI number...'}
                    </Styles.LoaderTextWrapper>
                )}
            </Styles.InputWrapper>
            <Styles.InputWrapper>
                <Input
                    className={''}
                    labelText={`Enter customer's mobile number`}
                    name={'phone_number'}
                    type={'text'}
                    leftElement={'+91'}
                    value={customerPhoneNumber}
                    onChange={(e) => setCustomerPhoneNumber(e.target.value)}
                    errMsg={validationErr.phone}
                    hasError={validationErr.phone.length > 0}
                />
            </Styles.InputWrapper>
            <Styles.InputWrapper>
                <Styles.CheckBoxFlex>
                    <Checkbox
                        isSelected={isChecked}
                        onSelectHandler={() => setIsChecked((s) => !s)}
                        color={'#0FA457'}
                    />
                </Styles.CheckBoxFlex>
                <Styles.ConfirmationText>{verifyLabel}</Styles.ConfirmationText>
            </Styles.InputWrapper>
            <Styles.ButtonWrapper>
                <PrimaryButton
                    disabled={isContinueButtonDisabled}
                    onClick={onSubmit}
                >
                    Continue
                </PrimaryButton>
            </Styles.ButtonWrapper>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </Styles.Wrapper>
    );
}

export default ScanIMEI;
